// utils/whatsappUtils.js - Production-ready WhatsApp integration

const WHATSAPP_WINDOW_NAME = 'whatsapp_bildi_tab';

/**
 * Opens WhatsApp Web with the given URL
 * Uses named window for optimal tab reuse when possible
 */
export const openWhatsApp = (messageUrl) => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // On mobile, open WhatsApp app directly
    if (isMobile) {
        return window.open(messageUrl, '_blank');
    }

    try {
        // Use named window for potential tab reuse
        const whatsappTab = window.open(messageUrl, WHATSAPP_WINDOW_NAME);
        
        if (whatsappTab) {
            whatsappTab.focus();
            return whatsappTab;
        } else {
            return null;
        }
    } catch (error) {
        return null;
    }
};

/**
 * Sends a WhatsApp message with optional phone number
 * @param {string} message - The message text to send
 * @param {string|null} phoneNumber - Optional phone number (will format automatically)
 * @returns {Window|null} - The opened window reference or null if blocked
 */
export const sendWhatsAppMessage = (message, phoneNumber = null) => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Simple cleaning - just fix the replacement characters that were causing issues
    let cleanMessage = message || 'הודעת תשלום מהמערכת';
    
    cleanMessage = cleanMessage
      .replace(/\uFFFD/g, '• ') // Replace replacement characters with bullet points
      .replace(/�/g, '• ') // Replace visual replacement character with bullet points  
      .replace(/%EF%BF%BD/gi, '• ') // Replace URL-encoded replacement characters with bullet points
      .trim();
    
    const encodedMessage = encodeURIComponent(cleanMessage);
    
    let whatsappUrl;
    
    if (phoneNumber) {
        // Clean and format phone number for Israeli numbers
        const cleanPhone = phoneNumber.replace(/\D/g, '');
        const formattedPhone = cleanPhone.startsWith('0') ? '972' + cleanPhone.substring(1) : cleanPhone;
        
        if (isMobile) {
            whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodedMessage}`;
        } else {
            whatsappUrl = `https://web.whatsapp.com/send?phone=${formattedPhone}&text=${encodedMessage}`;
        }
    } else {
        // No phone number - user will choose contact
        if (isMobile) {
            whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
        } else {
            whatsappUrl = `https://web.whatsapp.com/send?text=${encodedMessage}`;
        }
    }
    
    return openWhatsApp(whatsappUrl);
};

/**
 * Creates a formatted WhatsApp message for debt notification
 * @param {Object} apartmentData - Apartment and debt information
 * @param {Object} buildingData - Building information
 * @returns {string} - Formatted message text
 */
export const createDebtMessage = (apartmentData, buildingData) => {
    const buildingInfo = buildingData?.data?.buildingNumber || buildingData?.buildingNumber || 'הבניין';
    const apartmentInfo = `דירה ${apartmentData.apartmentNumber}`;
    const mainTenant = apartmentData.users[0];
    const tenantName = mainTenant?.name || 'דייר';
    
    const monthlyFee = apartmentData.vaadMonthly || 500;
    const unpaidCount = apartmentData.monthsUnpaidCount;
    const totalDebt = unpaidCount * monthlyFee;
    
    // Calculate building age for context
    let buildingAge = unpaidCount;
    if (buildingData?.data?.createDate || buildingData?.createDate || buildingData?.CreatedDate) {
        const buildingCreateDate = buildingData?.data?.createDate || buildingData?.createDate || buildingData?.CreatedDate;
        const currentDate = new Date();
        const createDate = new Date(buildingCreateDate);
        buildingAge = Math.ceil((currentDate - createDate) / (1000 * 60 * 60 * 24 * 30));
    }
    
    const unpaidMonthsList = apartmentData.unpaidMonths && apartmentData.unpaidMonths.length > 0
        ? apartmentData.unpaidMonths.slice(0, 6).join(', ') + (apartmentData.unpaidMonths.length > 6 ? '...' : '')
        : 'לא זמין';

    let message = `🏠 *ועד הבית ${buildingInfo}*\n\n`;
    message += `👋 שלום ${tenantName},\n\n`;
    message += `📋 *פרטי חוב עבור ${apartmentInfo}:*\n`;
    message += `${'─'.repeat(25)}\n\n`;

    message += `💰 *סיכום חוב:*\n`;
    message += `• דמי ועד חודשיים: ₪${monthlyFee.toLocaleString()}\n`;
    message += `• חודשים לא שולמו: ${unpaidCount} מתוך ${buildingAge}\n`;
    message += `• סכום חוב כולל: ₪${totalDebt.toLocaleString()}\n`;
    message += `• אחוז חוב: ${Math.round((unpaidCount / buildingAge) * 100)}%\n\n`;

    message += `📅 *חודשים שלא שולמו:*\n${unpaidMonthsList}\n\n`;

    if (apartmentData.users.length > 1) {
        message += `👥 *דיירי הדירה (${apartmentData.users.length}):*\n`;
        apartmentData.users.forEach((user, index) => {
            message += `${index + 1}. ${user.name}\n`;
        });
        message += `\n`;
    }

    message += `⚠️ *חשוב:*\n`;
    message += `יש לסדר את החוב בהקדם האפשרי.\n`;
    message += `לפרטי תשלום ותיאום, אנא צרו קשר.\n\n`;

    message += `📞 תודה על ההבנה והשיתוף פעולה\n`;
    message += `💻 מערכת ניהול ועד הבית`;

    return message;
};

/**
 * Creates a summary report of all unpaid apartments
 * @param {Array} unpaidApartments - Array of unpaid apartment data
 * @param {Object} buildingData - Building information
 * @param {Object} bankData - Bank account information from EditBuilding
 * @returns {string} - Formatted summary report
 */
export const createSummaryReport = (unpaidApartments, buildingData, bankData = null) => {
    const buildingInfo = buildingData?.data?.buildingNumber || buildingData?.buildingNumber || 'הבניין';
    
    const totalUnpaidApartments = unpaidApartments.length;
    const totalUnpaidMonths = unpaidApartments.reduce((sum, apt) => sum + apt.monthsUnpaidCount, 0);
    const totalDebt = unpaidApartments.reduce((sum, apt) => sum + (apt.totalDebt || (apt.monthsUnpaidCount * (apt.vaadMonthly || 500))), 0);
    const totalResidents = unpaidApartments.reduce((sum, apt) => sum + apt.usersCount, 0);

    let message = `🏠 *דוח חובות ועד הבית ${buildingInfo}*\n`;
    message += `📅 תאריך דוח: ${new Date().toLocaleDateString('he-IL')}\n`;
    message += `${'═'.repeat(35)}\n\n`;

    // Priority breakdown
    const highPriority = unpaidApartments.filter(apt => apt.monthsUnpaidCount >= 6);
    const mediumPriority = unpaidApartments.filter(apt => apt.monthsUnpaidCount >= 3 && apt.monthsUnpaidCount < 6);
    const lowPriority = unpaidApartments.filter(apt => apt.monthsUnpaidCount < 3);

    if (highPriority.length > 0 || mediumPriority.length > 0 || lowPriority.length > 0) {
        if (highPriority.length > 0) {
            message += `🔴 חוב גבוה (6+ חודשים): ${highPriority.length} דירות\n`;
        }
        if (mediumPriority.length > 0) {
            message += `🟡 חוב בינוני (3-5 חודשים): ${mediumPriority.length} דירות\n`;
        }
        if (lowPriority.length > 0) {
            message += `🟢 מספר דירות: ${lowPriority.length} דירות\n`;
        }
        message += `\n`;
        message += `📊 *סה"כ חוב משוער:${' '.repeat(12 - totalDebt.toLocaleString().length)}₪${totalDebt.toLocaleString()}*\n`;
        message += `${'─'.repeat(30)}\n\n`;
    }

    // Detailed apartment list - show all apartments sorted by apartment number
    message += `📋 *פירוט דירות חייבות:*\n\n`;
    const sortedApartments = [...unpaidApartments].sort((a, b) => {
        // Convert apartment numbers to integers for proper numeric sorting
        const numA = parseInt(a.apartmentNumber) || 0;
        const numB = parseInt(b.apartmentNumber) || 0;
        return numA - numB;
    });

    sortedApartments.forEach((apartment, index) => {
        const apartmentDebt = apartment.totalDebt || (apartment.monthsUnpaidCount * (apartment.vaadMonthly || 500));
        const aptNum = apartment.apartmentNumber.toString();
        const indexNum = (index + 1).toString();
        
        // Calculate spaces for proper alignment
        // For index < 10, add extra space to align with double-digit indices
        const indexPrefix = indexNum.length === 1 ? ' ' : '';
        // Add spaces before apartment number for consistent right alignment (assuming max 3 digits)
        const aptSpaces = ' '.repeat(Math.max(0, 3 - aptNum.length));

        message += `${indexPrefix}${indexNum}. *דירה ${aptSpaces}${aptNum}*\n`;
        message += `    💰 ${apartment.monthsUnpaidCount} חודשים (₪${apartmentDebt.toLocaleString()})\n`;

        if (apartment.usersCount > 1) {
            message += `    👥 ${apartment.usersCount} דיירים\n`;
        }
        message += `\n`;
    });

    // Bank account information section
    if (bankData && (bankData.accountNumber || bankData.bankNumber || bankData.bankName || bankData.bankAccountName)) {
        message += `${'─'.repeat(30)}\n\n`;
        message += `🏦 *פרטי חשבון להעברה:*\n`;
        
        if (bankData.accountNumber) {
            message += `• מספר חשבון: ${bankData.accountNumber}\n`;
        }
        
        if (bankData.bankNumber) {
            message += `• מספר סניף: ${bankData.bankNumber}\n`;
        }
        
        if (bankData.bankName) {
            message += `• שם הבנק: ${bankData.bankName}\n`;
        }
        
        if (bankData.bankAccountName) {
            message += `• שם בעל החשבון: ${bankData.bankAccountName}\n`;
        }
        
        message += `\n`;
    }

    message += `${'─'.repeat(30)}\n`;
    message += `💻 *נוצר ע"י bildiapp.com מערכת ניהול ועד הבית*\n`;
    message += `⏰ ${new Date().toLocaleString('he-IL')}`;

    return message;
};

/**
 * Creates a summary report of all unpaid apartments with phone numbers
 * @param {Array} unpaidApartments - Array of unpaid apartment data
 * @param {Object} buildingData - Building information
 * @param {Object} bankData - Bank account information from EditBuilding
 * @returns {string} - Formatted summary report with phone numbers
 */
export const createSummaryReportWithPhones = (unpaidApartments, buildingData, bankData = null) => {
    const buildingInfo = buildingData?.data?.buildingNumber || buildingData?.buildingNumber || 'הבניין';
    
    const totalUnpaidApartments = unpaidApartments.length;
    const totalUnpaidMonths = unpaidApartments.reduce((sum, apt) => sum + apt.monthsUnpaidCount, 0);
    const totalDebt = unpaidApartments.reduce((sum, apt) => sum + (apt.totalDebt || (apt.monthsUnpaidCount * (apt.vaadMonthly || 500))), 0);
    const totalResidents = unpaidApartments.reduce((sum, apt) => sum + apt.usersCount, 0);
    const totalPhoneNumbers = unpaidApartments.reduce((sum, apt) => sum + apt.users.filter(user => user.phone).length, 0);

    let message = `🏠 *דוח חובות עם מספרי טלפון - ועד הבית ${buildingInfo}*\n`;
    message += `📅 תאריך דוח: ${new Date().toLocaleDateString('he-IL')}\n`;
    message += `${'═'.repeat(35)}\n\n`;

    // Priority breakdown
    const highPriority = unpaidApartments.filter(apt => apt.monthsUnpaidCount >= 6);
    const mediumPriority = unpaidApartments.filter(apt => apt.monthsUnpaidCount >= 3 && apt.monthsUnpaidCount < 6);
    const lowPriority = unpaidApartments.filter(apt => apt.monthsUnpaidCount < 3);

    if (highPriority.length > 0 || mediumPriority.length > 0 || lowPriority.length > 0) {
        if (highPriority.length > 0) {
            message += `🔴 חוב גבוה (6+ חודשים): ${highPriority.length} דירות\n`;
        }
        if (mediumPriority.length > 0) {
            message += `🟡 חוב בינוני (3-5 חודשים): ${mediumPriority.length} דירות\n`;
        }
        if (lowPriority.length > 0) {
            message += `🟢 מספר דירות: ${lowPriority.length} דירות\n`;
        }
        message += `\n`;
        message += `📊 *סה"כ חוב משוער:${' '.repeat(12 - totalDebt.toLocaleString().length)}₪${totalDebt.toLocaleString()}*\n`;
        message += `📞 *סה"כ מספרי טלפון:${' '.repeat(8 - totalPhoneNumbers.toString().length)}${totalPhoneNumbers} מספרים*\n`;
        message += `${'─'.repeat(30)}\n\n`;
    }

    // Detailed apartment list with phone numbers - show all apartments sorted by apartment number
    message += `📋 *פירוט דירות חייבות עם מספרי טלפון:*\n\n`;
    const sortedApartments = [...unpaidApartments].sort((a, b) => {
        // Convert apartment numbers to integers for proper numeric sorting
        const numA = parseInt(a.apartmentNumber) || 0;
        const numB = parseInt(b.apartmentNumber) || 0;
        return numA - numB;
    });

    sortedApartments.forEach((apartment, index) => {
        const apartmentDebt = apartment.totalDebt || (apartment.monthsUnpaidCount * (apartment.vaadMonthly || 500));
        const aptNum = apartment.apartmentNumber.toString();
        const indexNum = (index + 1).toString();
        
        // Calculate spaces for proper alignment
        // For index < 10, add extra space to align with double-digit indices
        const indexPrefix = indexNum.length === 1 ? ' ' : '';
        // Add spaces before apartment number for consistent right alignment (assuming max 3 digits)
        const aptSpaces = ' '.repeat(Math.max(0, 3 - aptNum.length));

        message += `${indexPrefix}${indexNum}. *דירה ${aptSpaces}${aptNum}*\n`;
        message += `    💰 ${apartment.monthsUnpaidCount} חודשים (₪${apartmentDebt.toLocaleString()})\n`;

        // Show all residents with their phone numbers
        if (apartment.users && apartment.users.length > 0) {
            message += `    👥 דיירים (${apartment.users.length}):\n`;
            apartment.users.forEach((user, userIndex) => {
                message += `       ${userIndex + 1}. ${user.name}`;
                if (user.phone && user.phone.trim() !== '') {
                    message += ` - 📞 ${user.phone}`;
                } else {
                    message += ` - ❌ אין טלפון`;
                }
                message += `\n`;
            });
        }
        message += `\n`;
    });

    // Bank account information section
    if (bankData && (bankData.accountNumber || bankData.bankNumber || bankData.bankName || bankData.bankAccountName)) {
        message += `${'─'.repeat(30)}\n\n`;
        message += `🏦 *פרטי חשבון להעברה:*\n`;
        
        if (bankData.accountNumber) {
            message += `• מספר חשבון: ${bankData.accountNumber}\n`;
        }
        
        if (bankData.bankNumber) {
            message += `• מספר סניף: ${bankData.bankNumber}\n`;
        }
        
        if (bankData.bankName) {
            message += `• שם הבנק: ${bankData.bankName}\n`;
        }
        
        if (bankData.bankAccountName) {
            message += `• שם בעל החשבון: ${bankData.bankAccountName}\n`;
        }
        
        message += `\n`;
    }

    message += `${'─'.repeat(30)}\n`;
    message += `💻 *נוצר ע"י bildiapp.com מערכת ניהול ועד הבית*\n`;
    message += `⏰ ${new Date().toLocaleString('he-IL')}`;

    return message;
};

export default {
    openWhatsApp,
    sendWhatsAppMessage,
    createDebtMessage,
    createSummaryReport,
    createSummaryReportWithPhones
};